/**
 * Trees Inventory Panel Configuration
 *
 * @version 1.0
 * @updated 2026-02-27
 * @module core/modules/environmental/config
 *
 * @description
 * Configuración del panel de inventario de árboles para BaseInventoryPanel.
 * Define columnas, formatters, eventos y comportamientos específicos.
 */

import { useCallback, useState, useEffect } from 'react';
import { treesApi } from '../../../map-tools/trees-api';
import type { TreeResponse } from '../../../map-tools/trees-api';
import { useEnvironmentalElementColorsStore } from '../store/element-colors-store';
import type {
  InventoryPanelConfig,
  InventoryRow,
  InventoryDataHookResult,
} from '../../../layouts/panels/LeftPanel';

// ─────────────────────────────────────────
// Translation Maps
// ─────────────────────────────────────────

const CONDITION_LABELS: Record<string, string> = {
  excellent: 'Excelente',
  good:      'Bueno',
  fair:      'Regular',
  poor:      'Deficiente',
  critical:  'Crítico',
  dead:      'Muerto',
};

const RISK_LABELS: Record<string, string> = {
  low:      'Bajo',
  medium:   'Medio',
  high:     'Alto',
  critical: 'Crítico',
};

const PRIORITY_LABELS: Record<string, string> = {
  low:    'Baja',
  medium: 'Media',
  high:   'Alta',
  urgent: 'Urgente',
};

// ─────────────────────────────────────────
// Formatters
// ─────────────────────────────────────────

function translateCondition(v: string | null): string {
  if (!v) return '—';
  return CONDITION_LABELS[v] ?? v;
}

function translateRisk(v: string | null): string {
  if (!v) return '—';
  return RISK_LABELS[v] ?? v;
}

function translatePriority(v: string | null): string {
  if (!v) return '—';
  return PRIORITY_LABELS[v] ?? v;
}

// ─────────────────────────────────────────
// Data Hook Wrapper
// ─────────────────────────────────────────

/**
 * Hook wrapper que adapta treesApi a la interfaz esperada.
 */
function useTreesData(projectId: string): InventoryDataHookResult<TreeResponse> {
  const [data, setData] = useState<TreeResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!projectId) {
      setData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const trees = await treesApi.getByProject(projectId);
      setData(trees);
    } catch (err) {
      console.error('[useTreesData] Error loading trees:', err);
      setError('Error al cargar los árboles');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // Load on mount
  useEffect(() => {
    void reload();
  }, [reload]);

  const deleteTree = useCallback(async (id: string): Promise<boolean> => {
    try {
      await treesApi.delete(id);
      return true;
    } catch (err) {
      console.error('[useTreesData] Error deleting tree:', err);
      return false;
    }
  }, []);

  return {
    data,
    isLoading,
    error,
    reload,
    delete: deleteTree,
  };
}

// ─────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────

/**
 * Configuración completa del panel de inventario de árboles.
 */
export const treesInventoryConfig: InventoryPanelConfig<TreeResponse> = {
  // ── Identidad ──
  entityType: 'tree',
  displayName: 'Árboles',
  displayNameSingular: 'árbol',

  // ── Columnas narrow (sidebar colapsado) ──
  narrowColumns: [
    {
      key: 'code',
      label: 'Código',
      title: 'Código del árbol',
      width: '65px'
    },
    {
      key: 'name',
      label: 'Nombre',
      title: 'Nombre común',
      width: '1fr'
    },
  ],

  // ── Columnas wide (sidebar expandido) ──
  wideColumns: [
    {
      key: 'code',
      label: 'Cód',
      title: 'Código del árbol',
      width: '80px'
    },
    {
      key: 'name',
      label: 'Nombre',
      title: 'Nombre común',
      width: '1.5fr'
    },
    {
      key: 'species',
      label: 'Especie',
      title: 'Nombre científico',
      width: '1.5fr'
    },
    {
      key: 'condition',
      label: 'Estado',
      title: 'Condición general',
      width: '65px',
      format: translateCondition,
    },
    {
      key: 'risk',
      label: 'Riesgo',
      title: 'Nivel de riesgo',
      width: '50px',
      format: translateRisk,
    },
    {
      key: 'priority',
      label: 'Prior.',
      title: 'Prioridad de intervención',
      width: '50px',
      format: translatePriority,
    },
    {
      key: 'neighborhood',
      label: 'Barrio',
      title: 'Barrio o zona',
      width: '80px'
    },
  ],

  // ── Data fetching ──
  useData: useTreesData,

  // ── Row builder ──
  buildRow: (tree: TreeResponse): InventoryRow => {
    const d = tree.data ?? {};
    return {
      id:           tree.id,
      code:         tree.code ?? null,
      name:         (d.commonName as string | undefined)
                    ?? (d.name as string | undefined)
                    ?? null,
      species:      (d.scientificName as string | undefined) ?? null,
      condition:    (d.generalCondition as string | undefined) ?? null,
      risk:         (d.overallRiskLevel as string | undefined) ?? null,
      priority:     (d.priority as string | undefined) ?? null,
      neighborhood: (d.neighborhood as string | undefined) ?? null,
    };
  },

  // ── Map integration ──
  events: {
    select: 'tree-select',
    visibilityToggle: 'tree-visibility-toggle',
    hover: 'tree-hover',
    zoom: 'tree-zoom',
    created: 'tree-created',
    updated: 'tree-updated',
    deleted: 'tree-deleted',
  },

  getZoomTarget: (tree: TreeResponse) => ({
    type: 'point',
    coordinates: tree.coordinates,
  }),

  // ── Color store ──
  colorStore: {
    getColor: (key: string) => useEnvironmentalElementColorsStore.getState().getColor(key),
    setColor: (key: string, hex: string) => useEnvironmentalElementColorsStore.getState().setColor(key, hex),
    colorKey: 'trees',
  },

  // ── Panel derecho ──
  detailsPanelType: 'tree',

  // ── Módulo ──
  moduleId: 'environmental',
};
